import { useState, useEffect } from "react";
import { sellerService, tileService, accountService } from "../../services/enhancedCrudService";
import { toast } from "react-toastify";
import { Crop } from "../../shared/crop";
import { MAX_PRODUCT_IMAGE_SIZE } from "../../shared/constant";
import Select from "react-select";

const SellerProfile = () => {
  const [profile, setProfile] = useState(null);
  const [categories, setCategories] = useState([]);
  const [catalogs, setCatalogs] = useState([]);
  const [types, setTypes] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [categoryTypes, setCategoryTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [profileImagePreview, setProfileImagePreview] = useState(null);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);

  const SELLER_PLACE_CHOICES = [
    { value: "morbi", label: "Morbi" },
    { value: "than", label: "Than" },
    { value: "imported", label: "Imported" },
  ];


  // const sellerTypes = [
  //   { value: "manufacturer", label: "Manufacturer" },
  //   { value: "importer", label: "Importer" },
  //   { value: "stock_premium", label: "Stockiest Only Premium" },
  //   { value: "stock_other", label: "Stockiest Other Grade" },
  // ];

  const colourStyles = {
    menuList: (styles) => ({
      ...styles,
      background: "white",
      boxShadow: "none",
    }),
  };

  const fetchTypes = async () => {
    try {
      const response = await tileService.getTypes();
      if (response.types && response.types.length) {
        let mapTyped = response.types.map((row) => {
          return {
            label: row.name,
            value: row.id,
          };
        });
        setTypes(mapTyped);
      }
    } catch (error) {
      console.error("Error fetching types:", error);
    }
  };

  const fetchSizes = async () => {
    try {
      const response = await tileService.getSizes();
      if (response.types && response.types.length) {
        let mapTyped = response.types.map((row) => {
          return {
            label: row.name,
            value: row.id,
          };
        });
        setSizes(mapTyped);
      }
    } catch (error) {
      console.error("Error fetching sizes:", error);
    }
  };

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await sellerService.getProfile();
      const data = response.data;
      const mappedCategories = (data.categories || []).map((cat) => ({
        label: cat.name,
        value: cat.name,
      }));
      setProfile(data);
      setFormData({
        ...data,
        category_names: mappedCategories,
      });
    } catch (error) {
      console.error("Error fetching seller profile:", error);
      toast.error("Failed to load seller profile");
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    const response = await sellerService.getCategoryList();
    if (response && response.length > 0) {
      let cMap = response.map((row) => {
        return {
          label: row.name,
          value: row.name,
        };
      });
      setCategories(cMap);
    }
  };

  const fetchCatalogs = async () => {
    try {
      const response = await sellerService.getCatalogs();
      setCatalogs(response || []);
    } catch (error) {
      console.error("Error fetching catalogs:", error);
    }
  };

  const fetchStates = async () => {
    try {
      const response = await accountService.getStates();
      if (response && response.states && response.states.length > 0) {
        const mappedStates = response.states.map((state) => ({
          label: state.name,
          value: state.id,
        }));
        setStates(mappedStates);
      } else {
        // Try alternative response structure
        if (response && Array.isArray(response)) {
          const mappedStates = response.map((state) => ({
            label: state.name,
            value: state.id,
          }));
          setStates(mappedStates);
        } else {
          setStates([]);
        }
      }
    } catch (error) {
      console.error("Error fetching states:", error);
      setStates([]);
    }
  };

  const fetchDistricts = async (stateId) => {
    try {
      if (!stateId) {
        setDistricts([]);
        return;
      }
      const response = await accountService.getDistricts(stateId);
      if (response && Array.isArray(response) && response.length > 0) {
        const mappedDistricts = response.map((district) => ({
          label: district.name,
          value: district.id,
        }));
        setDistricts(mappedDistricts);
      } else if (response && response.districts && response.districts.length > 0) {
        // Fallback for different response structure
        const mappedDistricts = response.districts.map((district) => ({
          label: district.name,
          value: district.id,
        }));
        setDistricts(mappedDistricts);
      } else {
        setDistricts([]);
      }
    } catch (error) {
      console.error("Error fetching districts:", error);
      setDistricts([]);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith("address.")) {
      const addressKey = name.split(".")[1];
      setFormData((prev) => ({
        ...prev,
        address: {
          ...prev.address,
          [addressKey]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleStateChange = (selectedState) => {
    const stateId = selectedState?.value;
    const stateName = selectedState?.label;
    
    setFormData((prev) => ({
      ...prev,
      address: {
        ...prev.address,
        state: stateId || null,
        state_name_display: stateName || "",
        district: null, // Reset district when state changes
        district_name_display: "",
      },
    }));
    
    // Fetch districts for the selected state
    fetchDistricts(stateId);
  };

  const handleDistrictChange = (selectedDistrict) => {
    const districtId = selectedDistrict?.value;
    const districtName = selectedDistrict?.label;
    
    setFormData((prev) => ({
      ...prev,
      address: {
        ...prev.address,
        district: districtId || null,
        district_name_display: districtName || "",
      },
    }));
  };

  const isValidData = () => {
    if (!formData.firm_name || formData.firm_name.trim() === "") {
      toast.error("Company name is required");
      return false;
    }
    if (!formData.mobile_number || formData.mobile_number.trim() === "") {
      toast.error("Mobile number is required");
      return false;
    }
    if (!formData?.category_names || formData?.category_names?.length === 0) {
      toast.error("Please select at least one category");
      return false;
    }
    if (!formData.gst_number || formData.gst_number.trim() === "") {
      toast.error("GST number is required");
      return false;
    }
    // if (!formData.seller_type || formData.seller_type.trim() === "") {
    //   toast.error("Seller type is required");
    //   return false;
    // }
    if (formData.gst_number.length !== 15) {
      toast.error("GST Number must be exactly 15 characters long");
      return false;
    }
    const gstPattern = /^[0-9]{2}[0-9A-Z]{10}[0-9]{1}[0-9A-Z]{2}$/;
    if (!gstPattern.test(formData.gst_number)) {
      toast.error(
        "Invalid GST Number format. Please check the format: 2 digits (State) + 10 alphanumeric (PAN) + 1 digit (Entity) + 2 alphanumeric (Check sum)"
      );
      return false;
    }
    // Check if GST starts with "24" (Morbi state code)
    if (!formData.gst_number.startsWith("24")) {
      toast.error("You are outside of Morbi and you can log in to the app as a buyer.");
      return false;
    }

    if (
      !formData.address?.address_line ||
      formData.address.address_line.trim() === ""
    ) {
      toast.error("Address line is required");
      return false;
    }

    if (!formData.address?.road || formData.address.road.trim() === "") {
      toast.error("Road/Street is required");
      return false;
    }

    if (!formData.address?.city || formData.address.city.trim() === "") {
      toast.error("City is required");
      return false;
    }

    if (!formData.address?.district) {
      toast.error("District is required");
      return false;
    }

    if (!formData.address?.state) {
      toast.error("State is required");
      return false;
    }

    if (
      !formData.address?.pin_code ||
      formData.address.pin_code.trim() === ""
    ) {
      toast.error("Pin code is required");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValidData()) {
      return;
    }
    try {
      setLoading(true);
      const validCategories = (formData.category_names || []).filter(category => 
        category && category.value && category.value.trim() !== "" && category.value !== "None"
      );
      
      if (validCategories.length === 0) {
        toast.error("Please select at least one category");
        setLoading(false);
        return;
      }
      
      const payload = {
        firm_name: formData.firm_name,
        mobile_number: formData.mobile_number,
        office_mobile_number: formData.office_mobile_number || "",
        owner_name: formData.owner_name || "",
        // seller_type: formData.seller_type,
        gst_number: formData.gst_number,
        summary: formData.summary || "",
        categories: validCategories.map(cat => ({ name: cat.value })),
        address: {
          address_line: formData.address?.address_line || "",
          road: formData.address?.road || "",
          city: formData.address?.city || "",
          state: formData.address?.state || "",
          district: formData.address?.district || "",
          pin_code: formData.address?.pin_code || "",
        },
        tile_type: formData.tile_type || "",
        tile_size: formData.tile_size || "",
        seller_place: formData.seller_place || "",
      };
      
      
      await sellerService.updateProfile(payload, profile.id);
      fetchProfile();
      setEditing(false);
      toast.success("Profile updated successfully");
    } catch (error) {
      console.error("Error updating profile:", error);
      
      if (error.response && error.response.data) {
        const errors = error.response.data;
        if (
          error.response.data.non_field_errors &&
          error.response.data.non_field_errors.length
        ) {
          let message =
            error.response.data.non_field_errors.length > 0
              ? error.response.data.non_field_errors[0]
              : "Profile update failed. Please try again.";
          toast.error(message);
        } else {
          Object.keys(errors).forEach((field) => {
            const messages = errors[field];
            if (Array.isArray(messages)) {
              toast.error(field + ": " + messages[0]);
            } else if (typeof messages === 'string') {
              toast.error(field + ": " + messages);
            }
          });
        }
      } else {
        toast.error("Profile update failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleProfileImageCrop = (cropData) => {
    if (cropData && cropData.fileCropped && cropData.originalFile) {
      const croppedFile = new File(
        [cropData.fileCropped],
        cropData.originalFile.name,
        {
          type: cropData.originalFile.type,
          lastModified: Date.now(),
        }
      );
      const maxSize = MAX_PRODUCT_IMAGE_SIZE * 1024 * 1024;
      if (croppedFile.size > maxSize) {
        toast.error(
          `Profile image size should be less than ${MAX_PRODUCT_IMAGE_SIZE}MB`
        );
        return;
      }
      setProfileImagePreview(URL.createObjectURL(croppedFile));
      uploadProfileImage(croppedFile);
    }
  };

  const uploadProfileImage = async (file) => {
    try {
      setLoading(true);
      const form = new FormData();
      form.append("profile_image", file);
      await sellerService.changeProfileImage(form);
      toast.success("Profile image updated successfully");
      fetchProfile();
      if (profileImagePreview) {
        URL.revokeObjectURL(profileImagePreview);
        setProfileImagePreview(null);
      }
    } catch (error) {
      console.error("Error updating profile image:", error);
      toast.error("Failed to update profile image");
    } finally {
      setLoading(false);
    }
  };

  const formatAddress = (address) => {
    if (!address) return "N/A";
    const { address_line, road, city, district_name_display, state_name_display, pin_code } = address;
    return [address_line, road, city, district_name_display, state_name_display, pin_code]
      .filter(Boolean)
      .join(", ");
  };

  useEffect(() => {
    if (formData?.category_names) {
      setCategoryTypes(
        formData?.category_names.map((category) => {
          return category.value;
        })
      );
    } else {
      setCategoryTypes([]);
    }
  }, [formData?.category_names]);

  useEffect(() => {
    fetchCategories();
    fetchProfile();
    fetchCatalogs();
    fetchTypes();
    fetchSizes();
    fetchStates();
  }, []);

  // Fetch districts when profile loads if state is already selected
  useEffect(() => {
    if (formData?.address?.state) {
      // State is stored as ID in the profile data
      fetchDistricts(formData.address.state);
    }
  }, [formData?.address?.state]);

  useEffect(() => {
    return () => {
      if (profileImagePreview) {
        URL.revokeObjectURL(profileImagePreview);
      }
    };
  }, [profileImagePreview]);

  if (loading) {
    return (
      <div className="row">
        <div className="col-md-12">
          <div className="card">
            <div className="card-body">
              <div className="text-center">
                <div className="spinner-border" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="row">
      <div className="col-md-12">
        <style jsx>{`
          .instagram-profile-container {
            background: #fff;
            border-radius: 8px;
            overflow: hidden;
            box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
          }

          .profile-header {
            padding: 30px;
            border-bottom: 1px solid #e6e6e6;
            background: linear-gradient(135deg, #006666 0%, #004d4d 100%);
            color: white;
          }

          .profile-avatar-section {
            display: flex;
            justify-content: center;
            align-items: center;
          }

          .profile-avatar {
            width: 150px;
            height: 150px;
            border-radius: 50%;
            border: 4px solid rgba(255, 255, 255, 0.3);
            overflow: hidden;
            position: relative;
            background: rgba(255, 255, 255, 0.1);
          }

          .profile-info {
            padding-left: 20px;
          }

          .profile-name-section {
            margin-bottom: 20px;
          }

          .profile-name {
            font-size: 28px;
            font-weight: 600;
            margin-bottom: 15px;
            color: white;
          }

          .profile-stats {
            display: flex;
            gap: 30px;
            margin-bottom: 20px;
          }

          .stat-item {
            text-align: center;
          }

          .stat-number {
            display: block;
            font-size: 24px;
            font-weight: 700;
            color: white;
          }

          .stat-label {
            font-size: 14px;
            color: rgba(255, 255, 255, 0.8);
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }

          .profile-details {
            background: rgba(255, 255, 255, 0.1);
            padding: 20px;
            border-radius: 8px;
            backdrop-filter: blur(10px);
          }

          .detail-row {
            margin-bottom: 8px;
            font-size: 14px;
            color: rgba(255, 255, 255, 0.9);
          }

          .detail-row strong {
            color: white;
            margin-right: 8px;
          }

          .rating-section {
            display: flex;
            align-items: center;
            gap: 10px;
            margin-top: 10px;
          }

          .rating-section strong {
            color: white;
          }

          .stars {
            display: flex;
            gap: 2px;
          }

          // .stars i {
          //   color: #ffd700 !important;
          // }

          .catalogs-section {
            padding: 30px;
            background: #fafafa;
          }

          .catalogs-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 25px;
            padding-bottom: 15px;
            border-bottom: 2px solid #e6e6e6;
          }

          .catalogs-header h3 {
            margin: 0;
            font-size: 24px;
            font-weight: 600;
            color: #333;
          }

          .catalog-count {
            color: #666;
            font-size: 14px;
            font-weight: 500;
          }

          .catalogs-grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
            gap: 25px;
            justify-items: center;
          }

          .catalog-item {
            text-align: center;
            transition: transform 0.2s ease;
          }

          .catalog-item:hover {
            transform: translateY(-5px);
          }

          .catalog-circle {
            width: 100px;
            height: 100px;
            border-radius: 50%;
            background: linear-gradient(135deg, #006666 0%, #004d4d 100%);
            display: flex;
            align-items: center;
            justify-content: center;
            position: relative;
            margin-bottom: 10px;
            box-shadow: 0 4px 15px rgba(0, 102, 102, 0.3);
            transition: all 0.3s ease;
          }

          .catalog-circle:hover {
            box-shadow: 0 6px 20px rgba(0, 102, 102, 0.4);
            transform: scale(1.05);
          }

          .catalog-link {
            text-decoration: none;
            color: white;
            display: flex;
            align-items: center;
            justify-content: center;
            width: 100%;
            height: 100%;
            border-radius: 50%;
            position: relative;
            overflow: hidden;
          }

          .catalog-icon {
            font-size: 32px;
            color: white;
            transition: opacity 0.3s ease;
          }

          .catalog-overlay {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.7);
            display: flex;
            align-items: center;
            justify-content: center;
            opacity: 0;
            transition: opacity 0.3s ease;
            border-radius: 50%;
          }

          .catalog-circle:hover .catalog-overlay {
            opacity: 1;
          }

          .catalog-overlay i {
            font-size: 24px;
            color: white;
          }

          .catalog-name {
            font-size: 12px;
            color: #333;
            font-weight: 500;
            text-align: center;
            line-height: 1.3;
            max-width: 100px;
            word-wrap: break-word;
          }

          .no-catalogs {
            grid-column: 1 / -1;
            text-align: center;
            padding: 40px;
            color: #999;
          }

          .no-catalogs i {
            margin-bottom: 15px;
          }

          @media (max-width: 768px) {
            .profile-header {
              padding: 20px;
            }

            .profile-avatar {
              width: 120px;
              height: 120px;
            }

            .profile-name {
              font-size: 24px;
            }

            .profile-stats {
              gap: 20px;
            }

            .stat-number {
              font-size: 20px;
            }

            .catalogs-grid {
              grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
              gap: 20px;
            }

            .catalog-circle {
              width: 80px;
              height: 80px;
            }

            .catalog-icon {
              font-size: 24px;
            }

            .catalog-name {
              font-size: 11px;
              max-width: 80px;
            }
          }
        `}</style>
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h4 className="card-title">Seller Profile</h4>
            <div className="card-actions">
              {!editing && (
                <button
                  className="btn btn-primary"
                  onClick={() => setEditing(true)}
                >
                  Edit Profile
                </button>
              )}
            </div>
          </div>
          <div className="card-body">
            {editing ? (
              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-md-4">
                    <div className="form-group">
                      <label>
                        Company Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        name="firm_name"
                        value={formData.firm_name || ""}
                        onChange={handleInputChange}
                        disabled
                      />
                    </div>
                  </div>

                  <div className="col-md-4">
                    <div className="form-group">
                      <label>
                        Phone <span className="text-danger">*</span>
                      </label>
                      <input
                        type="tel"
                        className="form-control"
                        name="mobile_number"
                        value={formData.mobile_number || ""}
                        onChange={handleInputChange}
                        disabled 
                      />
                    </div>
                  </div>
              
                  <div className="col-md-4">
                    <div className="form-group">
                      <label>
                        GST Number <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        name="gst_number"
                        value={formData.gst_number || ""}
                        onChange={handleInputChange}
                        disabled 
                      />
                    </div>
                  </div>
                      <div className="col-md-4">
                    <div className="form-group">
                      <label>
                        Office Mobile Number
                      </label>
                      <input
                        type="tel"
                        className="form-control"
                        name="office_mobile_number"
                        value={formData.office_mobile_number || ""}
                        onChange={handleInputChange}
                        placeholder="Enter office mobile number"
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-group">
                      <label>
                        Owner Name
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        name="owner_name"
                        value={formData.owner_name || ""}
                        onChange={handleInputChange}
                        placeholder="Enter owner name"
                      />
                    </div>
                  </div>
                  {/* <div className="col-md-4">
                    <div className="form-group">
                      <label>
                        Seller Type <span className="text-danger">*</span>
                      </label>
                      <Select
                        styles={colourStyles}
                        options={sellerTypes}
                        value={sellerTypes.find(
                          (opt) => opt.value === formData.seller_type
                        )}
                        onChange={(selected) =>
                          setFormData((prev) => ({
                            ...prev,
                            seller_type: selected?.value || "",
                          }))
                        }
                        placeholder="Select seller type"
                        required
                      />
                    </div>
                  </div> */}
                  <div className="col-md-4">
                    <div className="form-group">
                      <label>
                        Address Line <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        name="address.address_line"
                        value={formData.address?.address_line || ""}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-group">
                      <label>
                        Road <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        name="address.road"
                        value={formData.address?.road || ""}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-group">
                      <label>
                        City <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        name="address.city"
                        value={formData.address?.city || ""}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>
                    <div className="col-md-4">
                    <div className="form-group">
                      <label>
                        State <span className="text-danger">*</span>
                      </label>
                      <Select
                        styles={colourStyles}
                        options={states}
                        value={states.find(
                          (state) => state.value === formData.address?.state
                        )}
                        onChange={handleStateChange}
                        placeholder="Select state"
                        required
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-group">
                      <label>
                        District <span className="text-danger">*</span>
                      </label>
                      <Select
                        styles={colourStyles}
                        options={districts}
                        value={districts.find(
                          (district) => district.value === formData.address?.district
                        ) || null}
                        onChange={handleDistrictChange}
                        placeholder="Select district"
                        isDisabled={!formData.address?.state}
                        required
                      />
                    </div>
                  </div>
                
                  <div className="col-md-4">
                    <div className="form-group">
                      <label>
                        Pin Code <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        name="address.pin_code"
                        value={formData.address?.pin_code || ""}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-group">
                      <label className="form-label">
                        Category <span className="text-danger">*</span>
                      </label>
                      <Select
                        options={categories}
                        value={formData.category_names}
                        onChange={(selectedOptions) => {
                          setFormData({
                            ...formData,
                            category_names: selectedOptions,
                          });
                        }}
                        isMulti
                        placeholder="Select categories"
                        required
                      />
                    </div>
                  </div>
                  {categoryTypes.includes("tiles") && (
                    <div className="col-md-4">
                      <div className="form-group">
                        <label>
                          Tile Type <span className="text-danger">*</span>
                        </label>
                        <Select
                          styles={colourStyles}
                          options={types}
                          value={types.find(
                            (opt) => opt.value === formData.tile_type
                          )}
                          onChange={(selected) =>
                            setFormData((prev) => ({
                              ...prev,
                              tile_type: selected?.value || "",
                            }))
                          }
                          required
                        />
                      </div>
                    </div>
                  )}
                  {categoryTypes.includes("tiles") && (
                    <div className="col-md-4">
                      <div className="form-group">
                        <label>
                          Tile Size <span className="text-danger">*</span>
                        </label>
                        <Select
                          styles={colourStyles}
                          options={sizes}
                          value={sizes.find(
                            (opt) => opt.value === formData.tile_size
                          )}
                          onChange={(selected) =>
                            setFormData((prev) => ({
                              ...prev,
                              tile_size: selected?.value || "",
                            }))
                          }
                          required
                        />
                      </div>
                    </div>
                  )}
                  {categoryTypes.includes("sanitaryware") && (
                    <div className="col-md-4">
                      <div className="form-group">
                        <label>
                          Seller Place <span className="text-danger">*</span>
                        </label>
                        <Select
                          styles={colourStyles}
                          options={SELLER_PLACE_CHOICES}
                          value={SELLER_PLACE_CHOICES.find(
                            (opt) => opt.value === formData.seller_place
                          )}
                          onChange={(selected) =>
                            setFormData((prev) => ({
                              ...prev,
                              seller_place: selected?.value || "",
                            }))
                          }
                          required
                        />
                      </div>
                    </div>
                  )}
                     <div className="col-md-8">
                  <div className="form-group">
                    <label>Bio</label>
                    <textarea
                      className="form-control"
                      name="summary"
                      value={formData.summary || ""}
                      onChange={handleInputChange}
                      rows="3"
                      maxLength="500"
                      placeholder="Enter a brief summary about your company..."
                    />
                  </div>
                </div>
                </div>
             
                <div className="text-end">
                  <button
                    type="submit"
                    className="btn btn-primary me-2"
                    disabled={loading}
                  >
                    {loading ? "Saving..." : "Save Changes"}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary ml-2"
                    onClick={() => {
                      setEditing(false);
                      setFormData(profile);
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <div className="instagram-profile-container">
                {/* Instagram-like Profile Header */}
                <div className="profile-header">
                  <div className="row">
                    <div className="col-md-3">
                      <div className="profile-avatar-section">
                        <div>
                          <Crop
                            cropShape="rect"
                            onChangeCropper={handleProfileImageCrop}
                            maxSizeMib={MAX_PRODUCT_IMAGE_SIZE}
                            height={200}
                            width={200}
                            aspectRatio={1}
                            imagePreview={
                              profileImagePreview || profile?.profile_image
                            }
                            isButtonHidden={false}
                            guid="seller-profile-image-crop"
                            buttonBackGroundColor="#f8f9fa"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-md-9">
                      <div className="profile-info">
                        <div className="profile-name-section">
                          <h2 className="profile-name">
                            {profile?.firm_name || "Company Name"}
                          </h2>
                          <div className="profile-stats text-white">
                            <div className="stat-item">
                              <span className="stat-number text-white">
                                {catalogs.length}
                              </span>
                              <span className="stat-label text-white">Catalogs</span>
                            </div>
                            <div className="stat-item">
                              <span className="stat-number text-white">
                                {profile?.post_counts || 0}
                              </span>
                              <span className="stat-label text-white">Posts</span>
                            </div>
                            <div className="stat-item">
                              <span className="stat-number text-white">
                                {profile?.average_rating || "0.0"}
                              </span>
                              <span className="stat-label text-white">Rating</span>
                            </div>
                          </div>
                        </div>
                        <div className="profile-details">
                          <div className="detail-row">
                            <strong>Owner:</strong>{" "}
                            {profile?.owner_name || "N/A"}
                          </div>
                          <div className="detail-row">
                            <strong>Type:</strong>{" "}
                            {profile?.seller_type || "N/A"}
                          </div>
                          <div className="detail-row">
                            <strong>Phone:</strong>{" "}
                            {profile?.mobile_number || "N/A"}
                          </div>
                          <div className="detail-row">
                            <strong>Office Mobile:</strong>{" "}
                            {profile?.office_mobile_number || "N/A"}
                          </div>
                          <div className="detail-row">
                            <strong>GST:</strong> {profile?.gst_number || "N/A"}
                          </div>
                          <div className="detail-row">
                            <strong>Categories:</strong>{" "}
                            {profile?.categories &&
                            profile.categories.length > 0
                              ? profile.categories
                                  .map((cat) => cat.name)
                                  .join(", ")
                              : "N/A"}
                          </div>
                          <div className="detail-row">
                            <strong>Address:</strong>{" "}
                            {formatAddress(profile?.address)}
                          </div>
                          <div className="detail-row">
                            <strong>Bio:</strong> {profile?.summary || "N/A"}
                            {!profile?.summary && (
                              <button
                                className="btn btn-sm ms-2"
                                onClick={() => setEditing(true)}
                                style={{ padding: '2px 6px', fontSize: '12px', backgroundColor: 'white', color: '#006666', border: '1px solid #006666' }}
                              >
                                <i className="fas fa-edit"></i>
                              </button>
                            )}
                          </div>
                          <div className="rating-section">
                            <strong>Rating:</strong>
                            <div className="stars">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <i
                                  key={star}
                                  className={`fas fa-star ${
                                    star <=
                                    Math.floor(
                                      parseFloat(profile?.average_rating || 0)
                                    )
                                      ? "text-warning"
                                      : "text-muted"
                                  }`}
                                  style={{ fontSize: "16px" }}
                                ></i>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Catalogs Section */}
                <div className="catalogs-section">
                  <div className="catalogs-header">
                    <h3>Catalogs</h3>
                    <span className="catalog-count">
                      {catalogs.length} catalogs
                    </span>
                  </div>
                  <div className="catalogs-grid">
                    {catalogs.length === 0 ? (
                      <div className="no-catalogs">
                        <i className="fas fa-file-pdf fa-3x text-muted mb-3"></i>
                        <p className="text-muted">No catalogs uploaded yet</p>
                      </div>
                    ) : (
                      catalogs.map((catalog, index) => (
                        <div key={catalog.id} className="catalog-item">
                          <div className="catalog-circle">
                            <a
                              href={catalog.file}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="catalog-link"
                            >
                              <div className="catalog-icon">
                                <i className="fas fa-file-pdf"></i>
                              </div>
                              <div className="catalog-overlay">
                                <i className="fas fa-eye"></i>
                              </div>
                            </a>
                          </div>
                          <div className="catalog-name">
                            {catalog.display_name || `Catalog ${index + 1}`}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SellerProfile;